import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  output,
  viewChild,
} from '@angular/core';
import { Instituto } from '../../../core/models/parametros/resgate-configuracao.model';
import { VivestListaVisivelDirective } from '../../../shared/directives/select-vivest/vivest-lista-visivel.directive';

/** Opção do `vivest-autocomplete` (design system). */
interface OpcaoAutocomplete {
  label: string;
  value: string;
  disabled?: boolean;
}

/** Propriedades do `vivest-autocomplete` usadas por este componente. */
type VivestAutocompleteElement = HTMLElement & { value: string; open: boolean };

/**
 * Busca de instituto ("Procure por instituto") sobre o `vivest-autocomplete` do design system:
 * lista todos os institutos, com os não habilitados desabilitados, e traduz a escolha em
 * `institutoSelecionado`.
 */
@Component({
  selector: 'app-instituto-filtro',
  standalone: true,
  imports: [VivestListaVisivelDirective],
  // Web components do design system Vivest (`vivest-autocomplete`, `vivest-button`).
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './instituto-filtro.component.html',
  styleUrl: './instituto-filtro.component.scss',
})
export class InstitutoFiltroComponent {
  private readonly campoInstituto =
    viewChild.required<ElementRef<VivestAutocompleteElement>>('campoInstituto');

  readonly institutos = input.required<readonly Instituto[]>();
  readonly carregando = input(false);
  /** Instituto já escolhido fora do filtro (ex.: "Configurar" da aba Instituto do plano). */
  readonly selecionado = input<string | null>(null);

  readonly institutoSelecionado = output<Instituto>();
  readonly limpo = output<void>();

  /** Institutos não habilitados aparecem na lista, mas o autocomplete não deixa escolhê-los. */
  readonly opcoes = computed<OpcaoAutocomplete[]>(() =>
    this.institutos().map((instituto) => ({
      value: instituto.tipo,
      label: instituto.nome,
      disabled: !instituto.habilitado,
    })),
  );

  constructor() {
    // Mostra no campo o instituto escolhido fora do filtro, assim que a lista estiver carregada.
    effect(() => {
      const tipo = this.selecionado();
      if (tipo && this.opcoes().some((opcao) => opcao.value === tipo)) {
        this.campoInstituto().nativeElement.value = tipo;
      }
    });
  }

  readonly placeholder = computed(() =>
    this.carregando() ? 'Carregando institutos…' : 'Digite o nome do instituto',
  );

  /** `vivest-select` traz a opção escolhida em `detail`. */
  aoSelecionar(event: Event): void {
    const opcao = (event as CustomEvent<OpcaoAutocomplete>).detail;
    const instituto = this.institutos().find((item) => item.tipo === opcao?.value);
    if (instituto?.habilitado) {
      this.institutoSelecionado.emit(instituto);
    }
  }

  limpar(): void {
    const campo = this.campoInstituto().nativeElement;
    campo.value = '';
    campo.open = false;
    this.limpo.emit();
  }
}
